import NextImage from 'next/image'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { StoryblokLink } from 'components/blocks/StoryblokLink'
import { CMSLink } from 'components/blocks/deprecated/CMSLink'
import { Icon } from 'components/ui'
import { ButtonLink } from 'components/ui/deprecated/ButtonLink'
import { Link } from 'components/ui/deprecated/Link'
import { OldButton } from 'components/ui/deprecated/OldButton'
import { Text } from 'components/ui/deprecated/Text'
import { useDataContext } from 'lib/contexts/dataContext'
import useOutsideClick from 'lib/hooks/useOutsideClick'
import {
  MenuEntryDividerStoryblok,
  TextLinkStoryblok,
} from 'lib/storyblok/types'
import { addAlphaToColor, dashedLine } from 'lib/style'
import { getStoryblokImageAttributes } from 'lib/utils/content'

type Props = {
  homeUrl: string
  onSearchClick: () => void
}

type DropdownProps = {
  label?: string
  links?: Array<TextLinkStoryblok | MenuEntryDividerStoryblok>
  component: 'text-link' | 'menu-entry' | 'two-column-menu-entry'
  link: TextLinkStoryblok
}

const DropdownComponent = ({ label, links }: DropdownProps): JSX.Element => {
  const router = useRouter()

  const wrapperRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  useOutsideClick(wrapperRef, () => setIsOpen(false))

  useEffect(
    function closeOnNavigate() {
      const handleRouteChange = () => {
        setIsOpen(false)
      }

      router.events.on('routeChangeStart', handleRouteChange)

      return () => {
        router.events.off('routeChangeStart', handleRouteChange)
      }
    },
    [router.events]
  )

  return (
    <div ref={wrapperRef}>
      {label && (
        <NavItem
          size="small"
          variant="text"
          onClick={() => setIsOpen((prev) => !prev)}
          rightIcon="caret-down"
        >
          {label}
        </NavItem>
      )}
      {isOpen && (
        <Dropdown>
          <div className="relative p-2 flex flex-col">
            {links?.map((link) => {
              if (link.component === 'menu-entry-divider') {
                return <Divider key={link._uid} />
              }
              return (
                <StoryblokLink
                  key={link._uid}
                  link={link.link}
                  className="text-foreground text-sixteen whitespace-pre-wrap p-2 rounded-2 transition-colors hover:bg-foreground/5 cursor-pointer"
                >
                  <Text as="span">{link.label}</Text>
                </StoryblokLink>
              )
            })}
          </div>

          <StyledBottom
            alt="Bottom banner"
            className="block"
            src="/assets/banner.png"
          />
        </Dropdown>
      )}
    </div>
  )
}

export const DesktopTopNav = ({
  homeUrl,
  onSearchClick,
  ...props
}: Props): JSX.Element | null => {
  const { config, isSubSite } = useDataContext()

  if (!config || !config?.content) {
    return null
  }

  const { menu_logo, menu_links, cta_label, cta_link } = config.content

  return (
    <Wrapper {...props}>
      <TopNavContainer>
        {menu_logo && menu_logo.filename && (
          <Logo>
            <Link href={homeUrl}>
              <NextImage
                {...getStoryblokImageAttributes(menu_logo)}
                className="object-cover h-full"
                priority
              />
            </Link>
          </Logo>
        )}
        <Nav>
          {menu_links?.map((item) => {
            if (item.component === 'text-link') {
              return (
                <NavItem key={item._uid} size="small" variant="text">
                  <CMSLink size="small" variant="text" href={item.link}>
                    <Text as="span">{item.label}</Text>
                  </CMSLink>
                </NavItem>
              )
            }
            return (
              <div key={item._uid} className="relative">
                <DropdownComponent
                  label={item.label}
                  links={item.links}
                  link={item.link}
                  component={item.component}
                />
              </div>
            )
          })}
        </Nav>

        {cta_link && cta_label && (
          <CMSLink
            size="small"
            href={cta_link}
            variant={isSubSite ? 'accent' : 'solid'}
            className="mr-2"
          >
            {cta_label}
          </CMSLink>
        )}

        <OldButton
          variant="text"
          size="small"
          className="w-12"
          onClick={() => onSearchClick()}
        >
          <Icon icon="search" size={20} />
        </OldButton>
      </TopNavContainer>
    </Wrapper>
  )
}

const StyledBottom = styled.img`
  object-fit: cover;
  height: 0.5rem;
`

const Divider = styled.hr`
  border: none;
  height: 1px;
  ${({ theme }) => dashedLine('bottom', theme.colors.foreground.default)}
`

const Wrapper = styled.header`
  width: 100%;
  ${({ theme }) => dashedLine('bottom', theme.colors.foreground.default)}
  background-color: ${({ theme }) => theme.colors.background.default};
`

const TopNavContainer = styled.div`
  max-width: 90rem;
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin: 0 auto 0 auto;

  padding: 0.5rem 1.25rem 0.5rem 1.25rem;
`

const Logo = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  img {
    max-height: 2.5rem;
    width: auto;
  }
`

const Dropdown = styled.ul`
  overflow: hidden;
  margin: auto;
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  z-index: ${({ theme }) => theme.zIndex.high};
  margin: auto;

  min-width: 10rem;
  max-width: 20rem;

  border-radius: 0.75rem;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  background: ${({ theme }) => theme.colors.background.default};
`

const Nav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  margin-left: 3rem;
  margin-right: auto;
`
const NavItem = styled(ButtonLink)`
  transition: background-color ${({ theme }) => theme.transitions.ease()};
  cursor: pointer;
  margin: 0 0.5rem;
  padding: 0 1rem;
  font-size: 1rem;

  :hover {
    opacity: 1;
    background-color: ${({ theme }) =>
      addAlphaToColor(theme.colors.foreground.default, 8)};
  }

  svg {
    opacity: 0.7;
  }
`
